import { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { isEqual } from 'lodash';
import { clampDepartureDate } from '@koob/ui/src/features/tripWizard';
import {
  toTripState,
  useDays,
  useSetLoading,
  useSetTripFilters,
  useTripDesignerConfig,
  useTripFilters,
  useTripStore,
} from '@koob/trip-designer';
import { getTrip } from '../../../api/trip';
import { getExperience } from '../../../api/experiences';
import { useLanguage } from '../../../hooks';
import { prefillDays } from './prefillDays';

/**
 * Loads the trip the composer opens and keeps the store in step with it: on
 * mount, when the current version changes, and when the trip duration changes.
 */
export function useComposeTrip({ tripId }) {
  const tripFilters = useTripFilters();
  const setLoading = useSetLoading();
  const setTripFilters = useSetTripFilters();
  const { lang } = useLanguage();
  const { api } = useTripDesignerConfig();

  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const setExperienceInStore = useTripStore(state => state.setExperience);
  const initializeTrip = useTripStore(state => state.initializeTrip);
  const setStoredTrip = useTripStore(state => state.setTrip);
  const setInitialExtras = useTripStore(state => state.setInitialExtras);

  // Flips once the store has been loaded (with real days or the
  // tripDuration-sized blank ones) at least once — ComposeMap's own
  // first-paint fallback only seeds/corrects the store before this happens,
  // so it never fights this effect's own writes once they land.
  const [hasHydrated, setHasHydrated] = useState(false);
  const [previousVersionId, setPreviousVersionId] = useState(null);

  const { data: tripData, reload: reloadTrip } = useAsync({
    promiseFn: getTrip,
    tripId,
    watchFn: (props, prevProps) => {
      return !isEqual(props.tripId, prevProps.tripId);
    },
  });
  const trip = tripData?.data;

  // KD-3656: the unsaved-changes check compares against the stored trip from
  // the store, so it also works from the navbar Exit off this page.
  useEffect(() => {
    if (trip) {
      setStoredTrip(trip);
    }
  }, [trip, setStoredTrip]);

  const currentVersion = trip?.currentVersion;
  const versions =
    trip?.versions
      ?.sort((a, b) => b.id - a.id)
      ?.map(v => {
        return {
          ...v,
          current: currentVersion?.id === v.id,
        };
      }) ?? [];

  // Formats `apiTrip` and puts it in the store. With no days from the API, only
  // `blankDaysFallback` seeds the tripDuration-sized blank ones.
  const applyTripToStore = async (apiTrip, { blankDaysFallback }) => {
    const {
      days: newDays,
      experiences: newExperiences,
      hotels: newHotels,
      initialExtras,
    } = await toTripState({
      trip: apiTrip,
      api,
      locale: tripFilters?.locale ?? lang,
    });

    if (newDays.length > 0) {
      // Keep every day whose experience actually loaded into the store,
      // even when it has no programs. A cross-organization experience of
      // a proposal can only be resolved from the trip's embedded copy
      // (which carries no programs), and must not be dropped here or the
      // pre-filled product would disappear from the itinerary.
      const filteredDays = newDays?.filter(d =>
        Boolean(newExperiences[d.experienceId]),
      );

      initializeTrip({
        days: filteredDays,
        experiences: newExperiences,
        hotels: newHotels,
      });
      setInitialExtras(initialExtras);
      setHasHydrated(true);
    } else if (blankDaysFallback) {
      // No days from API - initialize with empty days based on tripDuration
      const duration = Math.max(tripFilters?.tripDuration || 1, 1);

      initializeTrip({
        days: prefillDays([], duration),
        experiences: newExperiences,
        hotels: newHotels,
      });
      setInitialExtras(initialExtras);
      setHasHydrated(true);
    }
  };

  // Update trip days when the current version changes
  useEffect(() => {
    if (
      tripData &&
      previousVersionId &&
      previousVersionId !== currentVersion?.id
    ) {
      const newVersionNumber = trip?.currentVersion?.version;

      setLoading(true);
      getTrip({
        tripId: tripFilters?.tripId,
        versionId: newVersionNumber,
      })
        .then(res => applyTripToStore(res.data, { blankDaysFallback: false }))
        .finally(() => {
          setLoading(false);
        });

      setTripFilters(current => ({
        ...current,
        departureDate: clampDepartureDate(
          trip?.startDate ? new Date(trip.startDate) : null,
        ),
        tripDuration: trip?.duration,
      }));
    }

    setPreviousVersionId(currentVersion?.id);
  }, [currentVersion?.id]);

  // Helper to check if days have real content
  const daysHaveContent = days => {
    return days?.some(
      day =>
        (day?.experienceId !== null && experiencesMap?.[day.experienceId]) ||
        !!day?.hotelId,
    );
  };

  useEffect(() => {
    const initializeFromTrip = async () => {
      setLoading(true);
      try {
        await applyTripToStore(trip, { blankDaysFallback: true });
      } finally {
        setLoading(false);
      }
    };
    // When re-initialization is skipped, the experiences come from the
    // persisted store (localStorage) and their option configuration (extras,
    // suggested/included program options) may have changed on the DMC side
    // since the last visit. Refresh the experiences catalog from the API
    // without touching days, so user content (selected extras, rooms) is kept.
    const refreshStoredExperiences = async () => {
      const experienceIds = [
        ...new Set(days.map(day => day.experienceId).filter(Boolean)),
      ].filter(id => experiencesMap[id]?.isAccessible !== false);

      await Promise.all(
        experienceIds.map(async experienceId => {
          try {
            const experienceData = await getExperience({
              experienceId,
              locale: tripFilters?.locale ?? lang,
            });
            const freshExperience = experienceData?.data;

            if (freshExperience?.id) {
              setExperienceInStore(freshExperience.id, {
                ...experiencesMap[freshExperience.id],
                ...freshExperience,
              });
            }
          } catch {
            // Keep the stored experience if the refresh fails
          }
        }),
      );
    };

    // ONLY skip re-initialization if current days already have user content
    // This allows template data to load, but prevents wiping user-added experiences

    if (trip && trip.currentVersion && !daysHaveContent(days)) {
      initializeFromTrip();
    } else if (trip && trip.currentVersion) {
      refreshStoredExperiences();
    }
  }, [trip, tripFilters?.tripDuration]);

  return { trip, reloadTrip, versions, currentVersion, hasHydrated };
}
